import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UtilityService } from '@app/shared/services/utility.service';
import { Contact } from '@models/profile/model';
import { forkJoin, Subscription } from 'rxjs';

import { formatStringList } from '@app/shared/utils/strings.util';
import { Model } from 'src/app/shared/models/clux/model-legacy';
import { ContactQuery } from 'src/app/state';
import { CommonConstants } from '../../../shared/constants/common.constant';
import { EmitterService } from '../../../shared/services/emitter.service';
import { EmitterMessage } from '../../models/clux/model';
import { Document } from '../../models/uba/profileConfiguration/model';
import { ServiceFactory } from '../../services/service.factory';
import { DialogComponent } from '../upload-file/upload-file-messages/dialog.component';
import { PopoverTooltipComponent } from '../popover-tooltip/popover-tooltip.component';
import { NgClass } from '@angular/common';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-upload-files',
  templateUrl: './upload-files.component.html',
  styleUrls: ['./upload-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [PopoverTooltipComponent, NgClass, MatButton]
})
export class UploadFilesComponent implements OnInit, OnDestroy {
  private contactQuery = inject(ContactQuery);
  private dialog = inject(MatDialog);
  private emitterService = inject(EmitterService);
  public serviceFactory = inject(ServiceFactory);
  private utilityService = inject(UtilityService);

  @Input() public acceptedFileTypes: string; // comma-separated string of acceptable filetypes, e.g. '.csv,.pdf,.xls,.xlsx'
  @Input() public downloadTemplateDesc = '';
  @Input() public dragDropText = '';
  @Input() public showFileFormatText = true;
  @Input() public maxFileCount = 5;
  @Input() public maxFileSize = 10 * 1024 * 1024; // max file size in bytes, default 10MB
  @Input() public uploadHeader = '';
  @Input() public uploadSubHeader = '';
  @Input() public uploadingFileDialogTitle = 'UPLOADING FILES';
  @Input() public uploadingFileDialogDesc = 'This may take a minute...';

  @Output() public oncancel: EventEmitter<string> = new EventEmitter();
  @Output() public fileUploadSuccess: EventEmitter<Document[]> = new EventEmitter();

  public files: File[] = [];
  public onFiledragOver: boolean;
  public fileFormatText: string;
  public fileTypes: Model.UploadFileTypes[];
  public dataFileInfoText: string;
  public subscription = new Subscription();

  private acceptedExtensions: string[] = [];
  private contact: Contact;
  private dialogRef: MatDialogRef<object>;

  public ngOnInit(): void {
    this.checkRequiredFields();
    this.parseAcceptedFileTypes();
    this.initializeModel();

    this.fileFormatText = `File must be in a ${formatStringList(this.acceptedFileTypes, 'disjunction')} format and cannot exceed ${this.maxFileSize / 1024 / 1024} MB`;
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public browseFile(event: Event): void {
    if (this.contact) {
      const input = event.target as HTMLInputElement;
      if (!input.files || input.files.length === 0) {
        return;
      }

      this.addFiles(Array.from(input.files));
    }
  }

  public onFileDragOver(event: Event): void {
    event.preventDefault();
    this.onFiledragOver = true;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();

    if (this.contact && event.dataTransfer?.files) {
      this.addFiles(Array.from(event.dataTransfer.files));
    }
  }

  public addFiles(newFiles: File[]): void {
    const filesExceedingSizeLimit = [];
    const filesWithInvalidType = [];

    for (const file of newFiles) {
      if (this.files.length >= this.maxFileCount) {
        this.utilityService.showGrowlMessage(`You can only upload a maximum of ${this.maxFileCount} files`, false);
        window.scroll(0, 0);
        break;
      }

      if (file.size > this.maxFileSize) {
        filesExceedingSizeLimit.push(file.name);
        continue;
      }

      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!this.isFileTypeAccepted(fileExtension)) {
        filesWithInvalidType.push(file.name);
        continue;
      }

      this.files.push(file);
    }

    if (filesExceedingSizeLimit.length > 0) {
      const growlMessage = `${formatStringList(filesExceedingSizeLimit.join(','), 'conjunction')} ${filesExceedingSizeLimit.length === 1 ? 'exceeds' : 'exceed'} the ${this.maxFileSize / 1024 / 1024} MB size limit.
      Try reducing the file size or use the form below. If you continued to have issues, contact us.`;
      this.utilityService.showGrowlMessage(growlMessage, false);
    }

    if (filesWithInvalidType.length > 0) {
      const growlMessage =
        `${formatStringList(filesWithInvalidType.join(','), 'conjunction')} ${filesWithInvalidType.length === 1 ? 'has' : 'have'} an invalid file format. Accepted formats: ${formatStringList(this.acceptedFileTypes, 'disjunction')}`;

      // setTimeout here because, if this is the second growl message being shown, it will overwrite the first one without a delay. Need to give the browser's event loop enough time to process each EmitterService event in the UI
      setTimeout(() => {
        this.utilityService.showGrowlMessage(growlMessage, false);
      }, 100);
    }
  }

  public upload(): void {
    this.openUploadingDialog();
    this.uploadFiles();
  }

  public uploadFiles(): void {
    const profileId: string = this.contact.clientId;
    const uploadObservables = this.files.map((file) => {
      const fileType = file.name.substr(file.name.lastIndexOf('.') + 1);
      return this.serviceFactory.uploadDocument(file, profileId, file.name, fileType);
    });

    forkJoin(uploadObservables).subscribe({
      next: (documents) => {
        this.fileUploadSuccess.emit(documents);
      },
      error: (_error: unknown) => {
        const message: EmitterMessage = this.emitterService.getEmitterMessage(CommonConstants.errorCodes.fileUploadError, false);
        this.emitterService.on(CommonConstants.emitterKeys.loginErrors).emit(message);
        window.scroll(0, 0);
      },
      complete: () => {
        this.files = [];
        this.dialogRef.close();
      },
    });
  }

  public onCancel(): void {
    this.files = [];
    this.oncancel.emit();
  }

  public removeFile(index: number): void {
    this.files.splice(index, 1);
  }

  private checkRequiredFields(): void {
    if (!this.acceptedFileTypes) {
      throw new Error('Attribute acceptedFileTypes is required');
    }
  }

  private initializeModel(): void {
    if (sessionStorage.CL_LUD !== 'undefined') {
      this.contact = this.contactQuery.getActive();
    }
  }

  private openUploadingDialog(): void {
    this.dialogRef = this.dialog.open(DialogComponent, {
      disableClose: true,
      data: {
        title: this.uploadingFileDialogTitle,
        desc: this.uploadingFileDialogDesc,
        view: 'uploadingFile',
      },
    });
  }

  private parseAcceptedFileTypes(): void {
    this.acceptedExtensions = this.acceptedFileTypes
      .split(',')
      .filter((type) => type.length > 0)
      .map((type) => type.trim().toLowerCase());
  }

  private isFileTypeAccepted(extension: string): boolean {
    return this.acceptedExtensions.includes(extension);
  }
}
